import React, { useEffect, useState } from "react";
import { z } from "zod";
import Auth from "../Auth";

// Zod schemas for API response validation
const DailyUsageSchema = z.object({
  date: z.string(),
  total_tokens: z.number().optional(),
  tokens: z.number().optional(),
  input_tokens: z.number().optional(),
  output_tokens: z.number().optional(),
  generations_count: z.number().optional(),
});

const UsageDataSchema = z.object({
  daily_usage: z.array(DailyUsageSchema),
  total_days: z.number().optional(),
  generated_at: z.string().optional(),
});

// TypeScript types derived from zod schemas
type DailyUsage = z.infer<typeof DailyUsageSchema>;
type UsageData = z.infer<typeof UsageDataSchema>;

interface TooltipPosition {
  x: number;
  y: number;
}

function SeoTokenUsage(): JSX.Element {
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [hoveredTokenBar, setHoveredTokenBar] = useState<number | null>(null);
  const [hoveredGenBar, setHoveredGenBar] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ x: 0, y: 0 });

  // GPT-4o-mini pricing (from codebase analysis)
  const PROMPT_TOKEN_COST = 0.00000015; // $0.00015 per 1000 tokens
  const COMPLETION_TOKEN_COST = 0.0000006; // $0.0006 per 1000 tokens
  // Since we don't know the split, we'll assume completion tokens (higher cost)
  const TOKEN_COST = COMPLETION_TOKEN_COST;

  const fetchUsageData = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Use our backend proxy endpoint to avoid CORS issues
      const result = await Auth.fetchWithIdToken("/api/admin/seo-usage-stats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Validate the response with zod
      const validatedData = UsageDataSchema.parse(result);
      setData(validatedData);

      if (validatedData.generated_at) {
        setLastUpdated(new Date(validatedData.generated_at));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch usage data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageData();
  }, []);

  const calculateTotalTokens = (): number => {
    if (!data?.daily_usage) return 0;
    return data.daily_usage.reduce((total, day) => total + (day.total_tokens || day.tokens || 0), 0);
  };

  const calculateTotalInputTokens = (): number => {
    if (!data?.daily_usage) return 0;
    return data.daily_usage.reduce((total, day) => total + (day.input_tokens || 0), 0);
  };

  const calculateTotalOutputTokens = (): number => {
    if (!data?.daily_usage) return 0;
    return data.daily_usage.reduce((total, day) => total + (day.output_tokens || 0), 0);
  };

  const calculateTotalGenerations = (): number => {
    if (!data?.daily_usage) return 0;
    return data.daily_usage.reduce((total, day) => total + (day.generations_count || 0), 0);
  };

  const calculateAverageTokensPerGeneration = (): number => {
    const totalTokens = calculateTotalTokens();
    const totalGenerations = calculateTotalGenerations();
    return totalGenerations > 0 ? Math.round(totalTokens / totalGenerations) : 0;
  };

  const calculateTotalCost = (inputTokens = 0, outputTokens = 0, totalTokens = 0): number => {
    // If we have separate input/output tokens, use different rates
    if (inputTokens > 0 || outputTokens > 0) {
      return inputTokens * PROMPT_TOKEN_COST + outputTokens * COMPLETION_TOKEN_COST;
    }
    // Fallback to total tokens with completion rate (conservative estimate)
    return totalTokens * TOKEN_COST;
  };

  const calculateDayCost = (day: DailyUsage): number => {
    return calculateTotalCost(day.input_tokens || 0, day.output_tokens || 0, day.total_tokens || day.tokens || 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getMaxTokens = (): number => {
    if (!data?.daily_usage) return 0;
    return Math.max(...data.daily_usage.map((day) => day.total_tokens || day.tokens || 0));
  };

  const getMaxGenerations = (): number => {
    if (!data?.daily_usage) return 0;
    return Math.max(...data.daily_usage.map((day) => day.generations_count || 0));
  };

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
    type: 'token' | 'generation'
  ): void => {
    if (type === 'token') {
      setHoveredTokenBar(index);
    } else {
      setHoveredGenBar(index);
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
          paddingBottom: "16px",
          borderBottom: "2px solid #E5E7EB",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            SEO Brainstorm AI Token Usage
          </h1>
          {lastUpdated && (
            <div
              style={{
                fontSize: "14px",
                color: "#6B7280",
                marginTop: "4px",
              }}
            >
              Last updated: {lastUpdated.toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={fetchUsageData}
          disabled={loading}
          style={{
            padding: "12px 24px",
            backgroundColor: loading ? "#9CA3AF" : "#7C3AED",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
          }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Loading State */}
      {loading && !data && (
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            backgroundColor: "#F9FAFB",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
          }}
        >
          <div style={{ fontSize: "16px", color: "#6B7280" }}>Loading usage data...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "8px",
            color: "#DC2626",
            marginBottom: "24px",
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Summary Cards */}
      {data && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Days Tracked</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{data.total_days || 0}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Tokens Used</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{calculateTotalTokens().toLocaleString()}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Input Tokens</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{calculateTotalInputTokens().toLocaleString()}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Output Tokens</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{calculateTotalOutputTokens().toLocaleString()}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Chat Messages</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{calculateTotalGenerations().toLocaleString()}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Avg Tokens/Message</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#111827" }}>{calculateAverageTokensPerGeneration().toLocaleString()}</div>
          </div>

          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "14px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Cost (GPT-4o-mini)</div>
            <div style={{ marginTop: "8px", fontSize: "32px", fontWeight: "600", color: "#059669" }}>
              {formatCurrency(calculateTotalCost(calculateTotalInputTokens(), calculateTotalOutputTokens(), calculateTotalTokens()))}
            </div>
          </div>
        </div>
      )}

      {/* Bar Chart */}
      {data?.daily_usage && data.daily_usage.length > 0 && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3
            style={{
              margin: "0 0 24px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            Daily Token Usage
          </h3>

          <div
            style={{
              display: "flex",
              alignItems: "end",
              gap: "8px",
              height: "300px",
              padding: "20px 0",
              borderBottom: "1px solid #E5E7EB",
              overflowX: "auto",
            }}
          >
            {data.daily_usage.map((day, index) => {
              const maxTokens = getMaxTokens();
              const totalTokens = day.total_tokens || day.tokens || 0;
              const barHeight = maxTokens > 0 ? (totalTokens / maxTokens) * 250 : 0;

              return (
                <div
                  key={day.date}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "60px" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: `${barHeight}px`,
                      backgroundColor: hoveredTokenBar === index ? "#8B5CF6" : "#7C3AED",
                      borderRadius: "4px 4px 0 0",
                      position: "relative",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => handleMouseEnter(e, index, 'token')}
                    onMouseLeave={() => setHoveredTokenBar(null)}
                    onMouseMove={handleMouseMove}
                  />
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#6B7280",
                      marginTop: "8px",
                      textAlign: "center",
                      transform: "rotate(-45deg)",
                      transformOrigin: "center",
                      width: "60px",
                    }}
                  >
                    {formatDate(day.date)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart Legend */}
          <div style={{ marginTop: "16px", fontSize: "12px", color: "#6B7280" }}>
            Hover over bars for detailed information. Costs calculated using GPT-4o-mini pricing: input tokens ${(PROMPT_TOKEN_COST * 1000).toFixed(4)}/1K, output tokens $
            {(COMPLETION_TOKEN_COST * 1000).toFixed(4)}/1K.
          </div>
        </div>
      )}

      {/* Generations Chart */}
      {data?.daily_usage && data.daily_usage.length > 0 && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            padding: "24px",
            marginTop: "24px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3
            style={{
              margin: "0 0 24px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            Daily Chat Messages Count
          </h3>

          <div
            style={{
              display: "flex",
              alignItems: "end",
              gap: "8px",
              height: "300px",
              padding: "20px 0",
              borderBottom: "1px solid #E5E7EB",
              overflowX: "auto",
            }}
          >
            {data.daily_usage.map((day, index) => {
              const maxGenerations = getMaxGenerations();
              const generations = day.generations_count || 0;
              const barHeight = maxGenerations > 0 ? (generations / maxGenerations) * 250 : 0;

              return (
                <div
                  key={day.date}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "60px" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: `${barHeight}px`,
                      backgroundColor: hoveredGenBar === index ? "#10B981" : "#059669",
                      borderRadius: "4px 4px 0 0",
                      position: "relative",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => handleMouseEnter(e, index, 'generation')}
                    onMouseLeave={() => setHoveredGenBar(null)}
                    onMouseMove={handleMouseMove}
                  />
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#6B7280",
                      marginTop: "8px",
                      textAlign: "center",
                      transform: "rotate(-45deg)",
                      transformOrigin: "center",
                      width: "60px",
                    }}
                  >
                    {formatDate(day.date)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart Legend */}
          <div style={{ marginTop: "16px", fontSize: "12px", color: "#6B7280" }}>Hover over bars to see message count and efficiency metrics for each day.</div>
        </div>
      )}

      {/* Data Table */}
      {data?.daily_usage && data.daily_usage.length > 0 && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            marginTop: "24px",
            overflow: "hidden",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              padding: "20px 24px",
              backgroundColor: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Daily Usage Details
            </h3>
          </div>

          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#F9FAFB" }}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Total Tokens
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Input Tokens
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Output Tokens
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Messages
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Avg Tokens/Msg
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.daily_usage
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((day, index) => (
                    <tr
                      key={day.date}
                      style={{
                        borderBottom: index < data.daily_usage.length - 1 ? "1px solid #F3F4F6" : "none",
                      }}
                    >
                      <td style={{ padding: "16px 24px", fontWeight: "500" }}>{formatDate(day.date)}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace" }}>{(day.total_tokens || day.tokens || 0).toLocaleString()}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace" }}>{day.input_tokens !== undefined ? day.input_tokens.toLocaleString() : "—"}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace" }}>{day.output_tokens !== undefined ? day.output_tokens.toLocaleString() : "—"}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace" }}>{day.generations_count !== undefined ? day.generations_count.toLocaleString() : "—"}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace" }}>
                        {day.generations_count && day.generations_count > 0 ? Math.round((day.total_tokens || day.tokens || 0) / day.generations_count).toLocaleString() : "—"}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontFamily: "monospace", color: "#059669" }}>{formatCurrency(calculateDayCost(day))}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div
        style={{
          backgroundColor: "#F0F9FF",
          border: "1px solid #BAE6FD",
          borderRadius: "12px",
          padding: "20px",
          marginTop: "24px",
        }}
      >
        <div style={{ fontSize: "14px", color: "#0369A1", lineHeight: "1.5" }}>
          <strong>ℹ️ About this data:</strong>
          <br />
          • Data is fetched from the SEO Brainstorm AI usage tracking endpoint
          <br />
          • Tracks daily token usage (input/output), message counts, and costs
          <br />• Costs are calculated using GPT-4o-mini pricing: ${(PROMPT_TOKEN_COST * 1000).toFixed(4)}/1K input tokens, ${(COMPLETION_TOKEN_COST * 1000).toFixed(4)}/1K output tokens
          <br />
          • Message metrics help analyze efficiency and usage patterns
          <br />• All costs are in USD and updated in real-time
        </div>
      </div>

      {/* Custom Tooltips */}
      {hoveredTokenBar !== null && data?.daily_usage && (
        <div
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translateX(-50%) translateY(-100%)",
            backgroundColor: "#1F2937",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            whiteSpace: "pre-line",
            zIndex: 1000,
            pointerEvents: "none",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          {(() => {
            const day = data.daily_usage[hoveredTokenBar];
            const totalTokens = day.total_tokens || day.tokens || 0;
            const generations = day.generations_count || 0;
            const cost = calculateDayCost(day);
            const avgTokensPerGen = generations > 0 ? Math.round(totalTokens / generations) : 0;

            let tooltipText = `${formatDate(day.date)}\n${totalTokens.toLocaleString()} total tokens`;

            if (day.input_tokens !== undefined && day.output_tokens !== undefined) {
              tooltipText += `\n${day.input_tokens.toLocaleString()} input • ${day.output_tokens.toLocaleString()} output`;
            }

            if (generations > 0) {
              tooltipText += `\n${generations.toLocaleString()} messages (${avgTokensPerGen} avg tokens/msg)`;
            }

            tooltipText += `\n${formatCurrency(cost)}`;
            return tooltipText;
          })()}
        </div>
      )}

      {hoveredGenBar !== null && data?.daily_usage && (
        <div
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translateX(-50%) translateY(-100%)",
            backgroundColor: "#1F2937",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            whiteSpace: "pre-line",
            zIndex: 1000,
            pointerEvents: "none",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          {(() => {
            const day = data.daily_usage[hoveredGenBar];
            const generations = day.generations_count || 0;
            const totalTokens = day.total_tokens || day.tokens || 0;
            const avgTokensPerGen = generations > 0 ? Math.round(totalTokens / generations) : 0;

            let tooltipText = `${formatDate(day.date)}\n${generations.toLocaleString()} messages`;
            if (avgTokensPerGen > 0) {
              tooltipText += `\n${avgTokensPerGen} avg tokens/msg`;
            }
            return tooltipText;
          })()}
        </div>
      )}
    </div>
  );
}

export default SeoTokenUsage;
